<template>
  <div>
    <TableRemote
      isSearchable
      hideDefaultFooter
      showExpand
      customClass="associatedProducts"
      :items="cartOrders"
      :headers="tableHeaders"
      :title="$t('cart.pageName')"
      :showRowIcon="true"
      :verticalActionDots="true"
      :itemActions="getItemActions"
      :isCartSection="true"
      :isCustomIcon="true"
      :disablePagination="true"
      :pagination="pagination"
      :centerColumns="['action']"
      :notSortableColumns="['action']"
      :sortableColumns="['ordernumber', 'createdat', 'status']"
      @iconAction="onClickAction"
      @sendQueryParams="fetchQuery"
    >
      <template #item.orderNumber="{ item }">
        <span :class="item.status === 'Draft' ? 'status' : ''">
          {{ item.orderNumber }}
        </span>
      </template>
      <template #item.createdAt="{ item }">
        <span :class="item.status === 'Draft' ? 'status' : ''">
          {{ getDate(item.createdAt) }}
        </span>
      </template>
      <template #item.status="{ item }">
        <span :class="item.status === 'Draft' ? 'status' : ''">
          {{ item.status }}
        </span>
      </template>
      <template #item.po="{ item }">
        <div
          v-if="item.status === 'Draft'"
          class="po"
        >
          <VTextField
            flat
            :value="item.po"
            @blur="updatePoNumber(item, $event.target.value)"
          />
        </div>
        <div v-else>
          <span>
            {{ item.po }}
          </span>
        </div>
      </template>
      <template #item.note="{ item }">
        <div class="note">
          <span>{{ item.note }}</span>
        </div>
      </template>
      <template #expanded-item="props">
        <td :colspan="props.headers.length">
          <OrderDetails
            :products="props.item.products"
            :orderId="props.item.id"
            :siteId="site.id"
            :order="props.item"
          />
        </td>
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import OrderDetails from '@/views/cart/OrderDetails.vue';
import Vue from 'vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import NoteForm from '@/components/forms/NoteForm';
import TableRemote from '@/components/shared/content-views/VTableRemote';

export default {
    components: {
        TableRemote,
        OrderDetails,
        VTextField,
    },
    data() {
        return {
            queryParams: { page: 1, perpage: 25, search: '' },
            cartOrders: [],
            pagination: this.orderPagination || {},
            loading: false,
        };
    },
    computed: {
        ...mapGetters([
            'lang',
            'hasRole',
            'site',
            'team',
            'orderPagination',
            'orders',
            'hasPermission',
        ]),
        tableHeaders() {
            const headers = [
                {
                    text: this.$t('forms.labels.orderNumber'),
                    value: 'orderNumber',
                },
                {
                    text: this.$t('forms.labels.date'),
                    value: 'createdAt',
                    sortable: true,
                },
                {
                    text: this.$t('forms.labels.status'),
                    value: 'status',
                    sortable: true,
                },
                {
                    text: this.$t('forms.labels.poNumber'),
                    value: 'po',
                    sortable: false,
                },
                {
                    text: this.$t('forms.labels.orderNote'),
                    value: 'note',
                    sortable: false,
                },
            ];
            return headers;
        },
        getItemActions() {
            const actions = [];
            if (this.hasPermission('order.send')) {
                actions.push({
                    name: this.$t('forms.labels.addNote'),
                    icon: 'comment',
                    mode: 'comment',
                    iconAndTextColor: '#26384B',
                });
                actions.push({
                    name: this.$t('forms.labels.orderIcon'),
                    icon: 'local_shipping',
                    mode: 'local_shipping',
                    iconAndTextColor: '#26384B',
                    isEnable: !(this.team.warehouseEmail && this.team.warehouseEmail.length),
                });
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }
            return actions;
        },
    },
    watch: {
        orders: {
            async handler() {
                await this.fetchQuery();
            },
            deep: true,
        },
    },
    async created() {
        this.loading = true;
        await this.getOrders();
        this.loading = false;
    },
    methods: {
        ...mapActions({
            reloadOrders: 'getOrderItems',
        }),
        async getOrders() {
            this.loading = true;
            const { data } = await this.$rq.getOrders(this.site.id, this.queryParams);
            this.cartOrders = [...this.cartOrders, ...data.orders];
            this.pagination = data.pagination;
            this.loading = false;
        },
        async fetchQuery(queryParams = {}) {
            if (!this.loading) {
                this.queryParams = {
                    ...this.queryParams,
                    ...queryParams,
                    search: queryParams?.search || '',
                    page: 1,
                    order: this.preparOrderQueryParams(queryParams.order),
                };
                this.pagination = {};
                this.cartOrders = [];
                await this.getOrders();
            }
        },
        preparOrderQueryParams(value) {
            if (typeof value !== 'string') {
                return null;
            }

            const [name, order] = value.split('_');
            let newValue;
            switch (name) {
                case 'ordernumber':
                    newValue = 'orderNumber';
                    break;
                case 'createdat':
                    newValue = 'createdAt';
                    break;
                case 'status':
                    newValue = 'status';
                    break;
                default:
                    return null;
            }
            return `${newValue}_${order}`;
        },
        getDate(date) {
            return this.$options.filters.formatDate(date);
        },
        onClickAction(item, action) {
            switch (action.mode) {
                case 'local_shipping':
                    this.completeOrder(item);
                    break;
                case 'delete':
                    this.cancelOrder(item);
                    break;
                case 'comment':
                    this.addComment(item);
                    break;
                default:
                    break;
            }
        },
        async cancelOrder(item) {
            this.$root.$emit('showModal', {
                title: this.$t('cart.remove'),
                component: Vue.component('confirmRemove', ConfirmForm),
                payload: { msg: this.$t('cart.removeMsg') },
                onConfirm: () => this.deleteOrder(item.id),
                isForm: false,
            });
        },
        async completeOrder(item) {
            await this.$rq.confirmOrder(this.site.id, item.id);
            this.$toasted.global.toast_success({
                message: this.$t('cart.confirm'),
            });
            await this.fetchQuery();
        },
        async deleteOrder(orderId) {
            await this.$rq.cancelOrder(this.site.id, orderId);
            this.$toasted.global.toast_success({
                message: this.$t('cart.orderCancelled'),
            });
            await this.fetchQuery();
        },
        async addComment(item) {
            this.$root.$emit('showModal', {
                title: this.$t('cart.comment'),
                component: Vue.component('NoteForm', NoteForm),
                payload: { note: item.note },
                onConfirm: requestPayload => this.updateComment(requestPayload, item),
            });
        },
        async updateComment(note, item) {
            if (note.length && note !== item.note) {
                const { data } = await this.$rq.updateComment(this.site.id, item.id, { note });
                if (data) {
                    this.$toasted.global.toast_success({
                        message: this.$t('cart.commentUpdated'),
                    });
                    await this.fetchQuery();
                }
            }
        },
        async updatePoNumber(item, po) {
            if (po.length && po !== item.po) {
                const { data } = await this.$rq.updateComment(this.site.id, item.id, { po });
                if (data) await this.fetchQuery();
            }
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getOrders();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};

</script>
<style scoped lang="scss">
    .status {
        color: #EF4135;
    }
    .po {
        width: 100px;
    }
    .note{
        width: 300px;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
    }
</style>
